import { Gauge } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TruncatedText } from "@/components/ui/truncated-text";
import { cn } from "@/lib/utils";
import type { Keyword } from "@/types/keyword";
import { useGetKpsScoringConfig, type KpsScoringConfig } from "@/resources/configuration-settings/configuration-settings.api";
import {
  DEFAULT_KPS_MAX_SCORES,
  DEFAULT_RELEVANCE_PENALTY_MULTIPLIERS,
  KPS_MAX_SCORE,
  KPS_BREAKDOWN_UNRECONCILED_TEXT,
  formatKpsBreakdownAverageOverKeywords,
  formatKpsBreakdownPenaltyKeywordCount,
} from "./constants";
import { formatScore } from "./utils";
import { isKeywordMetricPending } from "@/utils/keywordPendingDisplay";

/**
 * The seven KPS factors, keyed as they are on the wire
 * (`components/rank-tracker.md § The KPS breakdown on the wire`) -- both `kpsBreakdown` (keyword)
 * and `kpsRollUp` (theme, product) carry exactly these seven keys.
 */
export type KpsBreakdownFactorKey =
  | "relevance"
  | "searchVolume"
  | "asinDensity"
  | "heroRank"
  | "searchTrend"
  | "keywordSales"
  | "competition";

export interface KpsBreakdownFactorValue {
  score: number | null;
  /** The row's own ceiling, or `null` -- falls back through `scoringConfig` then `DEFAULT_KPS_MAX_SCORES`. */
  max: number | null;
}

export interface KpsRollUpFactorValue extends KpsBreakdownFactorValue {
  /** This factor's own base -- can differ from the roll-up's `keywordCount` (FR-3's exclusion rule). */
  keywordCount: number;
}

/**
 * The view model this component renders, discriminated on `level`
 * (`decisions/the-kps-breakdown-renders-a-view-model.md`). Both screens (keyword bank, rank
 * tracker) build one of these; the component itself takes no `Keyword`.
 */
export interface KpsKeywordBreakdownViewModel {
  level: "keyword";
  total: number | null;
  label: string;
  factors: Record<KpsBreakdownFactorKey, KpsBreakdownFactorValue>;
  heroRelevanceFactor: number | null;
  includesCompetitorBrand: boolean;
  noOverlapWithHeroContent: boolean;
}

export interface KpsRollUpBreakdownViewModel {
  level: "rollUp";
  total: number | null;
  label: string;
  factors: Record<KpsBreakdownFactorKey, KpsRollUpFactorValue>;
  /** The row's own base -- `metrics.kps.keywordCount` on the wire, stated once. */
  keywordCount: number;
  penaltyKeywordCount: number;
}

export type KpsBreakdownViewModel = KpsKeywordBreakdownViewModel | KpsRollUpBreakdownViewModel;

/**
 * Builds the keyword-arm view model from a full `Keyword`, the only mapper this module exports --
 * the keyword bank's one render site (`KeywordMetricCells.tsx`) is the only place a `Keyword`
 * actually meets this component. Returns `null` for the `kps = -1` "pending enrichment" sentinel,
 * which is the "no trigger" rule moved out of the component and into this mapper -- the component
 * itself now just renders `null` when handed no view model.
 */
export function keywordToKpsBreakdown(keyword: Keyword): KpsKeywordBreakdownViewModel | null {
  if (isKeywordMetricPending(keyword.kps)) return null;

  return {
    level: "keyword",
    total: keyword.kps,
    label: keyword.search,
    factors: {
      relevance: { score: keyword.kps_hero_relevance_score, max: keyword.kps_max_hero_relevance_score ?? null },
      searchVolume: { score: keyword.kps_search_score, max: keyword.kps_max_search_score ?? null },
      asinDensity: { score: keyword.kps_asin_density_score, max: keyword.kps_max_asin_density_score ?? null },
      heroRank: { score: keyword.kps_hero_rank_score, max: keyword.kps_max_hero_rank_score ?? null },
      searchTrend: { score: keyword.kps_search_trend_score, max: keyword.kps_max_search_trend_score ?? null },
      keywordSales: { score: keyword.kps_keyword_sales_score, max: keyword.kps_max_keyword_sales_score ?? null },
      competition: { score: keyword.kps_competition_score ?? null, max: keyword.kps_max_competition_score ?? null },
    },
    heroRelevanceFactor: keyword.kps_hero_relevance_factor,
    includesCompetitorBrand: keyword.includes_competitor_brand,
    noOverlapWithHeroContent: keyword.no_overlap_with_hero_content,
  };
}

/**
 * The seven-entry literal factor definition list -- label, colour and the two `KpsScoringConfig`
 * keys each factor reads. Never index `factors` or `scoringConfig` with a variable key outside
 * this list: `noImplicitAny: false` means a foreign key resolves to `any` and nothing reports it.
 */
const KPS_FACTOR_DEFS = [
  { label: "Relevance", factorKey: "relevance", color: "hsl(var(--chart-6))", enabledKey: "relevance_enabled", configMaxKey: "relevance_max" },
  { label: "Search volume", factorKey: "searchVolume", color: "hsl(var(--chart-1))", enabledKey: "search_volume_enabled", configMaxKey: "search_volume_max" },
  { label: "ASIN density", factorKey: "asinDensity", color: "hsl(var(--chart-3))", enabledKey: "asin_density_enabled", configMaxKey: "asin_density_max" },
  { label: "Hero rank", factorKey: "heroRank", color: "hsl(var(--chart-2))", enabledKey: "hero_rank_enabled", configMaxKey: "hero_rank_max" },
  { label: "Search trend", factorKey: "searchTrend", color: "hsl(var(--chart-5))", enabledKey: "search_trend_enabled", configMaxKey: "search_trend_max" },
  { label: "Keyword sales", factorKey: "keywordSales", color: "hsl(var(--chart-4))", enabledKey: "keyword_sales_enabled", configMaxKey: "keyword_sales_max" },
  { label: "Competition", factorKey: "competition", color: "hsl(var(--chart-7))", enabledKey: "competition_enabled", configMaxKey: "competition_max" },
] as const satisfies readonly {
  label: string;
  factorKey: KpsBreakdownFactorKey;
  color: string;
  enabledKey: keyof KpsScoringConfig;
  configMaxKey: keyof KpsScoringConfig;
}[];

/** Looks up a factor's enable-flag key from the literal list above -- never an ad hoc index. */
function enabledKey(factorKey: KpsBreakdownFactorKey): keyof KpsScoringConfig {
  return KPS_FACTOR_DEFS.find((def) => def.factorKey === factorKey)!.enabledKey;
}

/**
 * FR-8b's predicate, keyword arm only. Exactly these three factors carry the data guard in
 * `06_calc_kps_for_keywords.sql:273-275`; `=== true` (not the component's own `!== false` factor
 * filter) so the sentence stays absent until the scoring config resolves --
 * `decisions/the-kps-breakdown-renders-a-view-model.md § The "does not reconcile" statement`.
 */
const KPS_BREAKDOWN_DATA_GUARDED_FACTORS: readonly KpsBreakdownFactorKey[] = ["asinDensity", "keywordSales", "competition"];

function factorsDoNotReconcile(
  factors: Record<KpsBreakdownFactorKey, KpsBreakdownFactorValue>,
  scoringConfig: KpsScoringConfig | undefined,
): boolean {
  return KPS_BREAKDOWN_DATA_GUARDED_FACTORS.some(
    (f) => scoringConfig?.[enabledKey(f)] === true && factors[f].score == null,
  );
}

interface KpsFactorRowData {
  label: string;
  factorKey: KpsBreakdownFactorKey;
  color: string;
  score: number | null;
  max: number;
}

function buildFactorRows(viewModel: KpsBreakdownViewModel, scoringConfig: KpsScoringConfig | undefined): KpsFactorRowData[] {
  return KPS_FACTOR_DEFS
    // A deactivated factor should not show a "0 / max" row. When the config is
    // still loading (undefined), keep every factor visible.
    .filter((def) => scoringConfig?.[def.enabledKey] !== false)
    .map((def) => {
      const factor = viewModel.factors[def.factorKey];
      const max = factor.max ?? scoringConfig?.[def.configMaxKey] ?? DEFAULT_KPS_MAX_SCORES[def.factorKey];

      return { label: def.label, factorKey: def.factorKey, color: def.color, score: factor.score, max };
    });
}

function KpsFactorRow({ label, score, max, color }: {
  label: string;
  score: number | null | undefined;
  max: number;
  color: string;
}) {
  const pct = score != null && Number.isFinite(score)
    ? Math.max(0, Math.min(100, (score / max) * 100))
    : 0;

  return (
    <div className="grid grid-cols-[7rem_1fr_auto] items-center gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="h-2 w-2 rounded-full shrink-0"
          style={{ backgroundColor: color }}
          aria-hidden
        />
        <TruncatedText className="text-xs text-foreground">{label}</TruncatedText>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="flex flex-col items-end w-12">
        <span className="text-xs tabular-nums text-foreground whitespace-nowrap">
          <span className="font-semibold">{formatScore(score)}</span>
          <span className="text-muted-foreground"> / {max}</span>
        </span>
      </span>
    </div>
  );
}

export interface KpsBreakdownPopoverProps {
  viewModel: KpsBreakdownViewModel | null;
  className?: string;
}

export function KpsBreakdownPopover({ viewModel, className }: KpsBreakdownPopoverProps) {
  const { data: scoringConfig } = useGetKpsScoringConfig();

  // The "render no trigger" rule is a wire fact now: a keyword with no kps or a roll-up with
  // nothing scored beneath it hands this component no view model at all.
  if (!viewModel) return null;

  const factorRows = buildFactorRows(viewModel, scoringConfig);

  const showsUnreconciledNote = viewModel.level === "keyword" && factorsDoNotReconcile(viewModel.factors, scoringConfig);

  const hasKeywordPenalty =
    viewModel.level === "keyword" &&
    (viewModel.includesCompetitorBrand || viewModel.noOverlapWithHeroContent);

  // AUT-376: name the multiplier the penalty actually applies, read from config. The note used to
  // render `heroRelevanceFactor`, which is `hero_relevance_adjusted / 100` -- the fraction scaling
  // the Relevance sub-score, not a penalty at all. Once the Rel. column started showing the raw
  // relevance that read as "51 reduced by 0.38x" (= 19.4) rather than the real 38. The multipliers
  // are org-configurable and cannot be recovered from the stored figures either:
  // CALC_KPS_FOR_KEYWORDS FLOORs at each step, so adjusted/raw only approximates them and drifts at
  // small relevances (hr 7 x 0.75 -> FLOOR(5.25) = 5, a ratio of 0.71). Both flags compound,
  // matching the procedure's FLOOR(FLOOR(hr x comp) x no_overlap).
  const keywordPenaltyMultiplier =
    viewModel.level === "keyword"
      ? (viewModel.includesCompetitorBrand
          ? scoringConfig?.competitor_brand_penalty_multiplier ??
            DEFAULT_RELEVANCE_PENALTY_MULTIPLIERS.competitorBrand
          : 1) *
        (viewModel.noOverlapWithHeroContent
          ? scoringConfig?.no_overlap_penalty_multiplier ?? DEFAULT_RELEVANCE_PENALTY_MULTIPLIERS.noOverlap
          : 1)
      : 1;

  const keywordPenaltyReasons =
    viewModel.level === "keyword"
      ? [
          viewModel.includesCompetitorBrand ? "includes a competitor brand name" : null,
          viewModel.noOverlapWithHeroContent ? "no overlap with hero content" : null,
        ].filter((reason): reason is string => reason != null)
      : [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`KPS score breakdown for "${viewModel.label}"`}
          className={cn(
            "inline-flex h-5 w-5 items-center justify-center rounded-full text-primary hover:text-foreground data-[state=open]:text-foreground hover:bg-primary/10 data-[state=open]:bg-primary/10 transition-colors",
            className,
          )}
        >
          <Gauge className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="right" className="w-80 p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">KPS Breakdown</span>
          <span className="text-2xl font-semibold tabular-nums text-foreground">
            {formatScore(viewModel.total)}
            <span className="text-base font-normal text-muted-foreground"> / {KPS_MAX_SCORE}</span>
          </span>
        </div>
        {viewModel.level === "rollUp" && (
          <p className="mb-2 text-[11px] text-muted-foreground">
            {formatKpsBreakdownAverageOverKeywords(viewModel.keywordCount)}
          </p>
        )}
        <div className="space-y-2">
          {factorRows.map((factor) => (
            <KpsFactorRow
              key={factor.factorKey}
              label={factor.label}
              score={factor.score}
              max={factor.max}
              color={factor.color}
            />
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-foreground">Total</span>
            <span className="font-semibold tabular-nums text-foreground">
              {formatScore(viewModel.total)} / {KPS_MAX_SCORE}
            </span>
          </div>
          {showsUnreconciledNote && (
            <p className="flex gap-1 pt-1 text-[11px] text-muted-foreground">
              <span>⚠</span>
              <span>{KPS_BREAKDOWN_UNRECONCILED_TEXT}</span>
            </p>
          )}
          {viewModel.level === "keyword" ? (
            hasKeywordPenalty ? (
              <p className="flex gap-1 pt-1 text-[11px] text-muted-foreground">
                <span>⚠</span>
                <span>
                  Relevance Score above is reduced ({keywordPenaltyMultiplier.toFixed(2)}× multiplier) — keyword{" "}
                  {keywordPenaltyReasons.join(" and ")}.
                </span>
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground mt-1">No penalties applied.</p>
            )
          ) : viewModel.penaltyKeywordCount > 0 ? (
            <p className="flex gap-1 pt-1 text-[11px] text-muted-foreground">
              <span>⚠</span>
              <span>{formatKpsBreakdownPenaltyKeywordCount(viewModel.penaltyKeywordCount, viewModel.keywordCount)}</span>
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-1">No penalties applied.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
